<template>
  <a-modal
    v-model="visible"
    :title="title"
    :destroyOnClose="true"
    @cancel="closeModal"
  >
    <div class="export-modal">
      <div v-if="step === 1 && !loading" class="d-flex flex-column" style="gap: 10px;">
        <div class="export-modal__info">
          <a-icon type="info-circle" class="font-size-18"  />
          <span>Current table data will be exported, including any applied filters.</span>
        </div>

        <span class="text-secondary">You can continue working — the file will download automatically when ready.</span>
      </div>

      <div v-if="loading || (step === 2 && !exportInfo.isDoneExport)" class="d-flex flex-column">
        <span class="d-block mb-1 font-size-16 font-weight-bold">Proccessing</span>
        <div class="d-flex align-items-center" style="gap: 20px;">
          <a-icon v-if="showExportLoading" type="loading" />
          <a-progress
            type="line"
            :percent="exportInfo.progress"
            :status="progressStatus"
          />
        </div>
        <div class="export-modal__info export-modal__info__blue mt-3">
          <a-icon type="info-circle" class="font-size-18"  />
          <span>Feel free to close this window and keep working — the file will continue preparing in the background.</span>
        </div>
      </div>

      <div v-if="(step === 2 && exportInfo.isDoneExport) && !loading" class="d-flex flex-column" style="gap: 10px;">
        <div class="export-modal__info export-modal__info__success">
          <a-icon type="check-circle" class="font-size-18 text-success" />
          <div class="d-flex flex-column">
            <span class="font-weight-bold">Export ready</span>
            <span class="text-secondary">{{ exportInfo.filename }}</span>
          </div>
        </div>
        <span class="text-secondary">Can't find the file? Check your Downloads folder or download again.</span>
      </div>
    </div>
    <template slot="footer">
      <a-button
        :type="loading ? 'danger' : 'default'"
        :ghost="loading"
        @click="closeModal"
      >
        {{ secondButtonText }}
      </a-button>
      <a-button
        :type="loading ? 'default' : 'primary'"
        :disabled="isExporting"
        @click="action"
      >
        {{ actionButtonText }}
      </a-button>
    </template>
  </a-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    targetId: {
      type: [String, null],
      default: null,
    },
  },
  components: {},
  data: () => ({
    visible: false,
    step: 1,
    exportInfo: {
      exportId: null,
      filename: '',
      progress: 0,
      exportStatus: 'pending',
      isDoneExport: false,
    },
  }),
  watch: {
    show: {
      handler(val) {
        this.visible = val

        if (!val) {
          this.resetState()
        }
      },
      immediate: true,
    },
  },
  computed: {
    ...mapGetters('modal', ['title', 'filter', 'loading']),
    actionButtonText() {
      if (this.loading) {
        return 'Continue working'
      }

      if (this.step === 1) {
        return 'Start export'
      }

      if (!this.exportInfo.isDoneExport) {
        return 'Continue working'
      }

      return 'Download'
    },
    secondButtonText() {
      if (this.loading) {
        return 'Cancel export'
      }
      if (this.step === 1) {
        return 'Cancel'
      }

      if (!this.exportInfo.isDoneExport) {
        return 'Cancel export'
      }

      return 'Done'
    },
    progressStatus() {
      if (this.exportInfo?.exportStatus === 'fail') {
        return 'exception'
      }

      if (!this.exportInfo?.isDoneExport) {
        return 'active'
      }

      return 'success'
    },
    showExportLoading() {
      return this.exportInfo.exportStatus !== 'fail' || !this.exportInfo.isDoneExport
    },
    isExporting() {
      return this.$store.getters['exports/isExporting'](this.exportInfo?.exportId)
    },
  },
  methods: {
    ...mapActions('exports', ['downloadExport']),
    async action() {
      if (this.loading || (this.step === 2 && !this.exportInfo.isDoneExport)) {
        this.closeModal()
        return
      }

      if (this.step === 1) {
        this.startExport()
        return
      }

      this.downloadFile()
    },
    async startExport() {
      if (!this.targetId) {
        return
      }

      try {
        const data = await this.$store.dispatch('modal/startExport', { id: this.targetId, filter: this.filter })

        if (this.exportInfo.progress && this.exportInfo.isDoneExport) {
          this.exportInfo.filename = data.filename
          this.exportInfo.exportId = data?.id
          this.step = 2
          return
        }

        this.exportInfo.filename = data.filename
        this.exportInfo.exportId = data?.id
        this.exportInfo.progress = data?.progress
        this.exportInfo.isDoneExport = data?.is_done
        this.exportInfo.exportStatus = data?.status
        this.step = 2
      } catch (e) {
        console.error(e)
      }
    },
    updateFileStatus(fileInfo) {
      this.exportInfo.progress = fileInfo.progress
      this.exportInfo.isDoneExport = fileInfo.is_done
    },
    downloadFile() {
      this.downloadExport({ id: this.targetId, exportId: this.exportInfo.exportId })
      this.closeModal()
    },
    resetState() {
      this.step = 1
      this.exportInfo = {
        exportId: null,
        filename: '',
        progress: 0,
        exportStatus: 'pending',
        isDoneExport: false,
      }
    },
    closeModal() {
      this.$emit('close')
    },
  },
}
</script>

<style lang="scss">
.export-modal {
  display: flex;
  justify-content: center;
  flex-direction: column;
  &__info {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 10px;
    border: 1px solid #262626;
    border-radius: 4px;
    &__blue {
      border-color: #2D8CFE;
      background-color: rgba(45, 140, 254, 0.3);
      color: #2D8CFE;
    }
    &__success {
      border-color: #F1FDF4;
      background-color: rgba(241, 253, 244, 0.7);
    }
  }
}
</style>
